import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, AuthContextType, LoginRequest, RegisterRequest } from '../types/auth';
import { authAPI } from '../services/api';
import { logger } from '../utils/logger';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(sessionStorage.getItem('access_token'));
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = sessionStorage.getItem('access_token');
      if (storedToken) {
        try {
          const userData = await authAPI.getCurrentUser();
          setUser(userData);
          setToken(storedToken);
        } catch (error) {
          logger.apiError('fetch user data during auth initialization', error);
          sessionStorage.removeItem('access_token');
          setToken(null);
        }
      }
      setIsLoading(false);
    };

    initAuth();
  }, []);

  const login = async (credentials: LoginRequest) => {
    try {
      const response = await authAPI.login(credentials);
      setUser(response.user);
      setToken(response.access_token);
      // Store token in sessionStorage with additional security measures
      sessionStorage.setItem('access_token', response.access_token);
      // Set token expiry check (optional additional security)
      sessionStorage.setItem('token_timestamp', Date.now().toString());
    } catch (error) {
      logger.apiError('user login', error, { email: credentials.email });
      throw error;
    }
  };

  const register = async (userData: RegisterRequest) => {
    try {
      await authAPI.register(userData);
      // After registration, automatically log in
      await login({ email: userData.email, password: userData.password });
    } catch (error) {
      logger.apiError('user registration', error, { email: userData.email, role: userData.role });
      throw error;
    }
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      logger.apiError('user logout', error);
    }
    
    setUser(null);
    setToken(null);
    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('token_timestamp');
  };

  const isAuthenticated = !!user && !!token;

  const updateUser = (updates: Partial<User>) => {
    if (user) {
      setUser({ ...user, ...updates });
    }
  };

  const value: AuthContextType = {
    user,
    token,
    login,
    register,
    logout,
    updateUser,
    isAuthenticated,
    isLoading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};